import React from "react";
import styled from "styled-components";
import useAxios from "axios-hooks";
import moment from "moment";

import { CHART_TYPE } from "../../constants";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.5rem 0 1rem 0;
  color: ${(props) => props.theme.textDark};
`;

const Label = styled.span`
  font-size: 0.9rem;
  color: gray;
`;

const Value = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
`;

// Hourly view = looking at one day -> that day's total; Daily view = looking
// at one month -> that month's total; Monthly view = looking at one year ->
// that year's total. Mirrors CHART_TYPE's own hourly/daily/monthly
// resolution naming, one level up (the PERIOD being summed, not the bucket
// size charted).
const STEP_TYPE_BY_CHART_TYPE = {
  [CHART_TYPE.Hourly]: "day",
  [CHART_TYPE.Daily]: "month",
  [CHART_TYPE.Monthly]: "year",
};

// Deliberately keyed by chartType, not stepType - the number itself still
// sums the period actually being viewed (a day/month/year, per
// STEP_TYPE_BY_CHART_TYPE above), but the label reads as "Hourly/Daily/
// Monthly production" to match the selected button's own name, not the
// (one-level-up) period it corresponds to - e.g. selecting "Monthly" shows
// "Monthly production", even though the number summed is that whole year's
// total (user preference, 2026-09-19 - the resolution/period distinction
// read as confusing in practice).
const LABEL_BY_CHART_TYPE = {
  [CHART_TYPE.Hourly]: "Hourly production",
  [CHART_TYPE.Daily]: "Daily production",
  [CHART_TYPE.Monthly]: "Monthly production",
};

// Same MWh-above-10000-kWh switch as CardFooter.js's production widgets -
// a turbine's monthly/yearly total can reach into the thousands/millions
// of kWh, same reasoning as there.
const ENERGY_MWH_THRESHOLD_KWH = 10000;
function formatEnergy(kwhValue) {
  if (typeof kwhValue !== "number" || Number.isNaN(kwhValue)) return null;
  const formatter = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });
  if (Math.abs(kwhValue) >= ENERGY_MWH_THRESHOLD_KWH) {
    return `${formatter.format(kwhValue / 1000)} MWh`;
  }
  return `${formatter.format(kwhValue)} kWh`;
}

// windTurbine can be momentarily undefined during navigation between two
// turbines' detail pages - see components/dashboard/CardHeader.js's guard
// for the crash this caused.
// energyScale = the kwh_meter register's scale (RAGO: 1000, counter in Wh):
// the server returns the raw counter difference, like CardFooter.js's
// widgets, which divide by it too.
const PeriodProduction = ({ windTurbine, chartType, date, energyScale = 1 }) => {
  const stepType = STEP_TYPE_BY_CHART_TYPE[chartType] || "day";
  const windTurbineId = windTurbine && windTurbine.id;

  const [{ data }] = useAxios(
    {
      url: `/productionData/period/${windTurbineId}`,
      params: {
        stepType,
        date: moment(date).toISOString(),
      },
    },
    { manual: !windTurbineId }
  );

  const formatted =
    data && typeof data.value === "number" ? formatEnergy(data.value / (energyScale || 1)) : null;
  if (!formatted) return null;

  return (
    <Wrapper>
      <Label>{LABEL_BY_CHART_TYPE[chartType] || "Production"}:</Label>
      <Value>{formatted}</Value>
    </Wrapper>
  );
};

export default PeriodProduction;
